/**
 * trendConfig.js — shared configuration for the Resident Trends v2 (episode)
 * components. Single source of truth for trigger/family display, dismiss
 * reasons, the suggested-action labels, and supporting_data -> metric-pill
 * mappings (keys must match what sp_detect_resident_call_trends writes).
 */

export const TRIGGER_CONFIG = {
    volume_spike:        { label: 'Volume Spike',        icon: '📈', color: '#e74c3c' },
    sustained_increase:  { label: 'Sustained Increase',  icon: '📊', color: '#e67e22' },
    high_frequency_new:  { label: 'New High-Frequency',  icon: '🔔', color: '#8e44ad' },
    nighttime_increase:  { label: 'Nighttime Increase',  icon: '🌙', color: '#2c3e50' },
    reason_shift:        { label: 'Reason Shift',        icon: '🔄', color: '#2980b9' },
    new_reason:          { label: 'New Reason',          icon: '⚠️', color: '#16a085' },
    device_implausible:  { label: 'Device Check',        icon: '🔧', color: '#7f8c8d' },
};

export const FAMILY_LABELS = {
    volume: 'Volume',
    nighttime: 'Nighttime',
    reason: 'Call Reasons',
    device: 'Device',
};

export const DISMISS_REASONS = [
    { value: 'not_applicable', label: 'Not applicable' },
    { value: 'already_addressed', label: 'Already addressed' },
    { value: 'expected_behavior', label: 'Expected behavior (mutes 90 days)', muteDays: 90 },
    { value: 'other', label: 'Other' },
];

// Resolve requires picking the action taken — this selection powers the
// Phase 3 effectiveness analytics.
export const ACTION_OPTIONS = [
    { value: 'care_plan_review', label: 'Care plan review' },
    { value: 'check_in', label: 'Checked in with resident' },
    { value: 'overnight_review', label: 'Overnight coverage review' },
    { value: 'care_level_assessment', label: 'Care-level assessment' },
    { value: 'device_check', label: 'Device inspection / replacement' },
    { value: 'verify_reason_coding', label: 'Verified reason coding' },
    { value: 'family_conference', label: 'Family conference' },
    { value: 'other', label: 'Other' },
];

export const ACTION_LABELS = {
    care_plan_review: 'Review the care plan',
    check_in: 'Check in with the resident and review recent care notes',
    overnight_review: 'Review overnight coverage and check in',
    care_level_assessment: 'Consider a care-level assessment',
    device_check: 'Inspect or replace the device',
    verify_reason_coding: 'Verify reason coding with the caregiver who logged it',
    family_conference: 'Discuss at the next family conference',
    other: 'Review with your care team',
};

export const STATUS_CONFIG = {
    new: { label: 'New', color: '#e74c3c' },
    acknowledged: { label: 'Acknowledged', color: '#e67e22' },
    in_progress: { label: 'In Progress', color: '#2980b9' },
    resolved: { label: 'Resolved', color: '#16a085' },
    dismissed: { label: 'Dismissed', color: '#7f8c8d' },
    auto_resolved: { label: 'Auto-Resolved', color: '#95a5a6' },
};

export const OUTCOME_CONFIG = {
    pending: { label: 'Outcome pending', color: '#7f8c8d' },
    improved: { label: 'Improved', color: '#16a085' },
    unchanged: { label: 'Unchanged', color: '#e67e22' },
    worsened: { label: 'Worsened', color: '#e74c3c' },
    recurred: { label: 'Recurred', color: '#8e44ad' },
};

export function scoreTier(score) {
    if (score >= 70) return { label: 'High', color: '#e74c3c' };
    if (score >= 40) return { label: 'Medium', color: '#e67e22' };
    return { label: 'Low', color: '#16a085' };
}

export function parseSupportingData(raw) {
    if (typeof raw === 'string') {
        try { return JSON.parse(raw); } catch { return {}; }
    }
    return raw || {};
}

/** Metric pills for one signal — keys match the detection SP output. */
export function getSignalMetrics(signal) {
    const sd = parseSupportingData(signal.supporting_data);
    switch (signal.trigger_type) {
        case 'volume_spike':
            return [
                { label: 'This Week', value: sd.current_week_count ?? '—' },
                { label: 'Avg/Week', value: sd.baseline_week_avg ?? '—' },
                { label: '% Increase', value: sd.percent_increase != null ? `${sd.percent_increase}%` : '—' },
            ];
        case 'sustained_increase':
            return [
                { label: 'This Week', value: sd.week1_current ?? '—' },
                { label: 'Weeks Rising', value: sd.consecutive_weeks_increasing ?? '—' },
            ];
        case 'high_frequency_new':
            return [
                { label: 'This Week', value: sd.current_week_count ?? '—' },
                { label: 'Prior Avg', value: sd.baseline_week_avg ?? '—' },
                { label: 'Facility P75', value: sd.facility_p75_current ?? '—' },
            ];
        case 'nighttime_increase':
            return [
                { label: 'Night Calls', value: sd.night_current_count ?? '—' },
                { label: 'Prior Avg', value: sd.night_baseline_week_avg ?? '—' },
                { label: 'Night %', value: sd.night_current_pct != null ? `${Math.round(sd.night_current_pct)}%` : '—' },
            ];
        case 'reason_shift':
            return [
                { label: 'Reason', value: sd.reason_name ?? '—' },
                { label: 'Now', value: sd.current_pct != null ? `${Math.round(sd.current_pct)}%` : '—' },
                { label: 'Was', value: sd.baseline_pct != null ? `${Math.round(sd.baseline_pct)}%` : '—' },
            ];
        case 'new_reason':
            return [
                { label: 'Reason', value: sd.reason_name ?? '—' },
                { label: 'Calls', value: sd.current_count ?? '—' },
                { label: '% Total', value: sd.current_pct != null ? `${Math.round(sd.current_pct)}%` : '—' },
            ];
        case 'device_implausible':
            return [
                { label: 'This Week', value: sd.current_week_count ?? '—' },
                { label: 'Per Day', value: sd.current_week_count != null ? Math.round(sd.current_week_count / 7) : '—' },
            ];
        default:
            return [];
    }
}

/** 5-week sparkline series for one signal's supporting_data. */
export function getSignalWeeklyData(signal) {
    const sd = parseSupportingData(signal.supporting_data);
    if (sd.week1 !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1].filter((v) => v !== undefined);
    if (sd.week1_current !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1_current].filter((v) => v !== undefined);
    if (sd.night_week1 !== undefined) return [sd.night_week5, sd.night_week4, sd.night_week3, sd.night_week2, sd.night_week1].filter((v) => v !== undefined);
    return [];
}

/** Episode-level sparkline: prefer a volume-family signal's weekly series. */
export function getEpisodeWeeklyData(episode) {
    const signals = episode.signals || [];
    const byPriority = ['volume_spike', 'high_frequency_new', 'sustained_increase', 'device_implausible', 'nighttime_increase'];
    for (const trigger of byPriority) {
        const sig = signals.find((s) => s.trigger_type === trigger);
        if (sig) {
            const weeks = getSignalWeeklyData(sig);
            if (weeks.length > 0) return weeks;
        }
    }
    return [];
}

export function getResidentDisplayName(episode) {
    const live = `${episode.resident_first_name || ''} ${episode.resident_last_name || ''}`.trim();
    return live || episode.resident_name_snapshot || 'Unknown resident';
}

export function getResidentRoom(episode) {
    return episode.resident_room || episode.room_snapshot || '';
}
